import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import DatePicker from "@/components/DatePicker";
import { Plus, FileCheck } from "lucide-react";
import { shortDate } from "@/lib/format";
import { toast } from "sonner";

const DOCS = ["Commercial Invoice", "Packing List", "Certificate of Origin", "BL Draft", "Fumigation Cert", "MSDS"];

export default function Bookings() {
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    supplier: "",
    commodity: "Furniture",
    origin_port: "CNSHA",
    destination_port: "AUSYD",
    incoterm: "FOB",
    container_type: "40HC",
    container_count: 1,
    cargo_ready_date: new Date().toISOString().slice(0, 10),
    requested_etd: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    notes: "",
  });

  const load = () => {
    api.get("/bookings").then(r => setItems(r.data));
    api.get("/purchase-orders").then(r => setPos(r.data));
  };
  useEffect(load, []);

  const submit = async () => {
    try {
      await api.post("/bookings", form);
      toast.success("Booking request submitted");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not submit booking");
    }
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="bookings-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Bookings</h1>
          <p className="text-sm text-zinc-500 mt-1">Online booking control with cut-off alerts, document tracking and PO linkage.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-booking-btn" className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-11"><Plus className="w-4 h-4 mr-2" />New booking</Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-2xl" data-testid="new-booking-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">New Booking Request</DialogTitle>
              <DialogDescription className="text-xs text-zinc-500">Submit a booking to your forwarder. Documents checklist is generated automatically.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label className="data-label">Supplier</Label><Input data-testid="bk-supplier" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} className="rounded-none mt-1.5" /></div>
              <div><Label className="data-label">Commodity</Label><Input data-testid="bk-commodity" value={form.commodity} onChange={e => setForm({ ...form, commodity: e.target.value })} className="rounded-none mt-1.5" /></div>
              <div>
                <Label className="data-label">Incoterm</Label>
                <Select value={form.incoterm} onValueChange={v => setForm({ ...form, incoterm: v })}>
                  <SelectTrigger data-testid="bk-incoterm" className="rounded-none mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["FOB","EXW","CIF","FCA","DAP","CIP"].map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="data-label">Origin Port</Label><Input data-testid="bk-origin" value={form.origin_port} onChange={e => setForm({ ...form, origin_port: e.target.value })} className="rounded-none mt-1.5" /></div>
              <div><Label className="data-label">Destination Port</Label><Input data-testid="bk-dest" value={form.destination_port} onChange={e => setForm({ ...form, destination_port: e.target.value })} className="rounded-none mt-1.5" /></div>
              <div>
                <Label className="data-label">Container Type</Label>
                <Select value={form.container_type} onValueChange={v => setForm({ ...form, container_type: v })}>
                  <SelectTrigger data-testid="bk-ctype" className="rounded-none mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["20GP","40GP","40HC","45HC"].map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="data-label">Container Count</Label><Input data-testid="bk-count" type="number" min={1} value={form.container_count} onChange={e => setForm({ ...form, container_count: Number(e.target.value) })} className="rounded-none mt-1.5" /></div>
              <div><Label className="data-label">Cargo Ready Date</Label><DatePicker testId="bk-ready" value={form.cargo_ready_date} onChange={(v) => setForm({ ...form, cargo_ready_date: v })} placeholder="Select ready date" /></div>
              <div><Label className="data-label">Requested ETD</Label><DatePicker testId="bk-etd" value={form.requested_etd} onChange={(v) => setForm({ ...form, requested_etd: v })} placeholder="Select ETD" /></div>
              <div className="col-span-2"><Label className="data-label">Notes</Label><Textarea data-testid="bk-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-none mt-1.5" rows={2} /></div>
            </div>
            <DialogFooter>
              <Button data-testid="bk-submit" onClick={submit} className="rounded-none bg-[#002FA7] hover:bg-[#002480]">Submit booking</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* PO readiness */}
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200">
          <h3 className="font-heading text-lg font-bold tracking-tight">PO Readiness Pipeline</h3>
          <p className="text-xs text-zinc-500 mt-1">Cargo ready status from suppliers — drives booking timing.</p>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                {["PO #", "Supplier", "Commodity", "Cartons", "CBM", "Value", "Ready Date", "Status", "Shipment"].map(h => <th key={h} className="px-4 py-2.5 data-label">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {pos.slice(0, 12).map(p => (
                <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                  <td className="px-4 py-2.5 metric font-medium">{p.id}</td>
                  <td className="px-4 py-2.5">{p.supplier}</td>
                  <td className="px-4 py-2.5">{p.commodity}</td>
                  <td className="px-4 py-2.5 metric">{p.cartons}</td>
                  <td className="px-4 py-2.5 metric">{p.cbm}</td>
                  <td className="px-4 py-2.5 metric">${p.value_usd.toLocaleString()}</td>
                  <td className="px-4 py-2.5 metric">{shortDate(p.cargo_ready_date)}</td>
                  <td className="px-4 py-2.5"><StatusBadge value={p.readiness_status} /></td>
                  <td className="px-4 py-2.5 metric text-zinc-500 text-xs">{p.linked_shipment || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New bookings table */}
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold tracking-tight">Booking Requests</h3>
          <span className="text-xs text-zinc-500 metric">{items.length} total</span>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                {["Booking #", "Supplier", "Lane", "Containers", "Incoterm", "Ready", "ETD", "Status", "Docs"].map(h => <th key={h} className="px-4 py-2.5 data-label">{h}</th>)}
              </tr>
            </thead>
            <tbody data-testid="bookings-tbody">
              {items.map(b => (
                <tr key={b.id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                  <td className="px-4 py-2.5 metric font-medium">{b.id}</td>
                  <td className="px-4 py-2.5">{b.supplier}</td>
                  <td className="px-4 py-2.5 text-zinc-700">{b.origin_port} <span className="text-zinc-400">→</span> {b.destination_port}</td>
                  <td className="px-4 py-2.5 metric">{b.container_count} × {b.container_type}</td>
                  <td className="px-4 py-2.5"><span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 bg-zinc-100 border border-zinc-200">{b.incoterm}</span></td>
                  <td className="px-4 py-2.5 metric">{shortDate(b.cargo_ready_date)}</td>
                  <td className="px-4 py-2.5 metric">{shortDate(b.requested_etd)}</td>
                  <td className="px-4 py-2.5"><StatusBadge value={b.status} /></td>
                  <td className="px-4 py-2.5 text-xs text-zinc-500"><FileCheck className="w-3.5 h-3.5 inline mr-1" />{(b.documents_received || []).length}/{(b.documents_required || DOCS).length}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={9} className="p-10 text-center text-sm text-zinc-500">No bookings yet — submit your first request.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
